import { useState } from "react";
import { Card } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calculator as CalcIcon, HelpCircle } from "lucide-react";

type SubscriptionPlan = 'PERSONAL' | 'PRO' | 'STUDIO' | 'ENTERPRISE';

interface TokenCalculatorProps {
  availableCredits: number;
  currentPlan: SubscriptionPlan;
}

const PLAN_LABELS: Record<SubscriptionPlan, string> = {
  PERSONAL: "Personal",
  PRO: "Pro",
  STUDIO: "Studio",
  ENTERPRISE: "Enterprise",
};

const PLAN_CONSUMPTION_RATE_PER_HOUR: Record<SubscriptionPlan, number> = {
  PERSONAL: 125,
  PRO: 110,
  STUDIO: 105,
  ENTERPRISE: 105,
};
const PLAN_HIGH_PARTICIPANT_RATE_PER_HOUR: Record<SubscriptionPlan, number> = {
  PERSONAL: 250,
  PRO: 225,
  STUDIO: 200,
  ENTERPRISE: 200,
};
const MAX_PARTICIPANTS_AT_BASE_RATE = 4;

const TokenCalculator = ({ availableCredits, currentPlan }: TokenCalculatorProps) => {
  const [duration, setDuration] = useState("1");
  const [participants, setParticipants] = useState("3");
  const [quality, setQuality] = useState("flow-studio");
  const planHourlyRate = PLAN_CONSUMPTION_RATE_PER_HOUR[currentPlan] || PLAN_CONSUMPTION_RATE_PER_HOUR.PERSONAL;
  const planHighParticipantHourlyRate = PLAN_HIGH_PARTICIPANT_RATE_PER_HOUR[currentPlan] || PLAN_HIGH_PARTICIPANT_RATE_PER_HOUR.PERSONAL;
  const participantsNum = Math.max(0, parseInt(participants, 10) || 0);
  const activeHourlyRate = participantsNum > MAX_PARTICIPANTS_AT_BASE_RATE
    ? planHighParticipantHourlyRate
    : planHourlyRate;

  const calculateCredits = () => {
    const durationNum = parseFloat(duration) || 0;
    const durationInMinutes = durationNum * 60;
    
    // Pricing formula: participants × minutes × rate
    // Credit rate is plan-based for 1-4 participants, and tiered by plan for 5+ participants.
    const creditRate = activeHourlyRate / 60;
    
    const rawCredits = participantsNum * durationInMinutes * creditRate;
    if (rawCredits <= 0) {
      return 0;
    }
    return Math.max(1, Math.floor(rawCredits));
  };

  const formatDuration = () => {
    const durationNum = parseFloat(duration) || 0;
    const hours = Math.floor(durationNum);
    const minutes = Math.round((durationNum % 1) * 60);
    if (hours === 0) return `${minutes} mins`;
    if (minutes === 0) return `${hours} hours`;
    return `${hours} hours ${minutes} mins`;
  };

  const estimatedCost = calculateCredits();

  return (
    <div className="space-y-4">
      <Card className="p-8 bg-card border-border">
        <div className="space-y-6">
          {/* Token Estimation Notice */}
          <div className="px-4 py-3 rounded-lg bg-muted/30 border border-border">
            <p className="text-sm text-muted-foreground">
              <span className="font-semibold text-foreground">Cost Estimation</span> - Calculate costs before starting your session
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              {PLAN_LABELS[currentPlan]} plan: {planHourlyRate} credits/viewer/hr for 1-4 viewers, and {planHighParticipantHourlyRate} credits/viewer/hr for 5+ viewers
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              Current applied rate: <span className="font-medium text-foreground">{activeHourlyRate}</span> credits per viewer per hour
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="duration">Expected Duration (hours)</Label>
            <Input
              id="duration"
              type="number"
              step="0.5"
              value={duration}
              onChange={(e) => setDuration(e.target.value)}
              className="bg-background border-border"
              placeholder="1"
            />
            <p className="text-xs text-muted-foreground">
              Use .5 for half-hour increments (e.g., 2.5 = 2 hours 30 mins)
            </p>
            {duration && (
              <p className="text-sm font-medium text-foreground">
                Duration: {formatDuration()}
              </p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="participants">Number of Viewers</Label>
            <Input
              id="participants"
              type="number"
              value={participants}
              onChange={(e) => setParticipants(e.target.value)}
              className="bg-background border-border"
              placeholder="5"
            />
          </div>

          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <Label htmlFor="quality">Streaming Quality</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <button className="text-muted-foreground hover:text-foreground transition-colors">
                    <HelpCircle className="w-4 h-4" />
                  </button>
                </PopoverTrigger>
                <PopoverContent className="w-80">
                  <div className="space-y-3">
                    <h4 className="font-semibold text-sm">Flow Credits Pricing</h4>
                    <div className="space-y-2 text-xs">
                      <div className="p-2 rounded-md bg-muted/30">
                        <div className="font-medium text-foreground mb-1">Tiered Credit Rate ({PLAN_LABELS[currentPlan]})</div>
                        <div className="text-muted-foreground">1-4 viewers: {planHourlyRate} credits per viewer per hour</div>
                        <div className="text-muted-foreground">5+ viewers: {planHighParticipantHourlyRate} credits per viewer per hour</div>
                        <div className="text-muted-foreground mt-2">
                          Current estimate: {participantsNum} viewers × 1 hour = {participantsNum * activeHourlyRate} FC
                        </div>
                      </div>
                      <div className="p-2 rounded-md bg-muted/30">
                        <div className="font-medium text-foreground mb-1">Base Rate</div>
                        <div className="text-muted-foreground">$1 USD = 100 Flow Credits</div>
                      </div>
                    </div>
                  </div>
                </PopoverContent>
              </Popover>
            </div>
            <Select value={quality} onValueChange={setQuality}>
              <SelectTrigger id="quality" className="bg-background border-border">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="flow-studio">Flow Studio (Recommended)</SelectItem>
                <SelectItem value="flow-standard">Flow Standard (Coming Soon)</SelectItem>
                <SelectItem value="flow-lite">Flow Lite (Coming Soon)</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Cost Estimate */}
          <div className="p-6 rounded-lg bg-muted/50 border border-border space-y-3 mt-8">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <CalcIcon className="w-4 h-4" />
              <span>Estimated Cost</span>
            </div>
            <div className="flex items-baseline gap-2">
              <span className="text-4xl font-bold text-secondary">
                {estimatedCost.toLocaleString()}
              </span>
              <span className="text-muted-foreground">Flow Credits</span>
            </div>
            <div className="text-sm">
              Available: <span className="font-semibold text-green-500">{availableCredits.toLocaleString()} Flow Credits</span>
            </div>
          </div>

        </div>
      </Card>
    </div>
  );
};

export default TokenCalculator;
